import { request } from "EntitiesApi/Actions";
import {
  accountSchema,
  companyApplicationSchema,
  employeePolicySchema,
  employerFormSchema,
  employeeFormSchema,
  companyProfileSchema,
  companyPolicySchema,
  employeeDependentSchema,
  invoiceSchema,
  carrierConfigurationSchema,
  recommendationSurveySchema,
  claimSchema,
} from "EntitiesApi/schemas";
import companyToken from "Utils/companyToken";

import type { RequestCallbacks } from "EntitiesApi/Actions";
import type { Schema } from "normalizr";

type ApiOptions = RequestCallbacks & {
  include?: Array<string>;
  page?: number;
  per?: number;
};

// This method will append the 'include' param to the end of the url.
const formatUrl = (url: string, include: Array<string> | null | undefined): string => {
  if (include) {
    url += formatIncludeFlags(include);
  }

  return url;
};

// This method formats the array of strings into the 'include' param
// to be used in an url.
const formatIncludeFlags = (includeFlags: Array<string>): string => `?include=${includeFlags.join(",")}`;

// This method will replace the ':id' placeholder in an url with the
// given id.
const makeUrl = (url: string, id: string | null | undefined): string => {
  if (id) {
    url = url.replace(":id", id);
  }

  return url;
};

// Proxy for the 'START_REQUEST' action.
const apiRequest = (url: string, schema: any, meta: RequestCallbacks) =>
  request(
    {
      url,
      method: "get",
      responseSchema: schema,
    },
    meta,
  );

// Creates a function that will accept parameters and meta information
// to be added to the request. It requires an initial url and a schema.
const makeApiGetter =
  <T>(url: string, schema: Schema<T>) =>
  ({ include, ...meta }: ApiOptions = {}) =>
    apiRequest(formatUrl(url, include), schema, meta);

// Creates a function that will accept an id, parameters, and meta information
// to be added to the request. It requires an initial url and a schema.
// The initial url should have an ':id' placeholder which will be replaced by
// the id passed in the returned function.
const makeApiIdGetter =
  <T>(url: string, schema: Schema<T>) =>
  (id: string, { include, page, per, ...meta }: ApiOptions = {}) => {
    let fullUrl = formatUrl(makeUrl(url, id), include);

    // Minimal inline pagination (no new helpers needed)
    if (page || per) {
      const params = new URLSearchParams();
      if (page) params.set("page", String(page));
      if (per) params.set("per", String(per));
      fullUrl += (fullUrl.includes("?") ? "&" : "?") + params.toString();
    }

    return apiRequest(fullUrl, schema, meta);
  };

// Any API endpoint should be created using either the 'makeApiGetter' or
// 'makeApiIdGetter' methods and exported here to be available for any React/Redux
// that uses the EntitiesApi.
export const getAccount = makeApiGetter("/api/v2/accounts/current", accountSchema);
export const getCompanyApplicationByCompanyProfileId = makeApiIdGetter("/api/v2/company-profiles/:id/current-company-application", companyApplicationSchema);
export const getAccountCompanyProfileById = makeApiIdGetter("/api/v2/accounts/show/:id", accountSchema);
export const getAccountWithAllCompanyProfiles = makeApiGetter("/api/v2/accounts/all_profiles", accountSchema);
export const getEmployeePolicyById = makeApiIdGetter("/api/v2/employee-policies/:id", employeePolicySchema);
export const getCompanyPolicyById = makeApiIdGetter("/api/v2/company_policies/:id", companyPolicySchema);
export const getEmployeePoliciesByCompanyPolicyId = makeApiIdGetter("/api/v2/company_policy/:id/employee_policies", companyPolicySchema);
export const getEmployeeFormsByCompanyProfileId = makeApiIdGetter("/api/v2/company_profiles/:id/employee_forms", companyProfileSchema);
export const getCompanyProfileById = makeApiIdGetter("/api/v2/company-profiles/:id", companyProfileSchema);
/** @deprecated Do not use - dead code and schema missing */
export const getPlanSuiteByCompanyPolicyId = makeApiIdGetter(
  "/api/v2/company-policy/:id/plan-suite",
  // NOTE: Where is the schema for this???
);
export const getCarrierConfigById = makeApiIdGetter("/api/v2/carrier-configurations/:id", carrierConfigurationSchema);
export const getAllCarrierConfigs = makeApiGetter("/api/v2/carrier-configurations/all", [carrierConfigurationSchema]);
export const getRecommendationSurveyByCompanyProfileId = makeApiIdGetter("/api/v2/company-profiles/:id/recommendation-survey", recommendationSurveySchema);
export const updateRecommandationSurvey = (company_profile_id: string, values: unknown, meta: RequestCallbacks = {}) =>
  request(
    {
      url: `/api/v2/company-profiles/${company_profile_id}/recommendation-survey`,
      method: "post",
      body: {
        recommendation_survey: values,
      },
      responseSchema: recommendationSurveySchema,
    },
    meta,
  );
export const createCarrierConfig = (context: string, values: unknown, meta: RequestCallbacks = {}) =>
  request(
    {
      url: `/api/v2/carrier-configurations/create`,
      method: "post",
      body: {
        context,
        carrier_configurations: values,
      },
      responseSchema: carrierConfigurationSchema,
    },
    meta,
  );
export const updateCarrierConfig = (context: string, values: unknown, meta: RequestCallbacks = {}) =>
  request(
    {
      url: `/api/v2/carrier-configurations/update`,
      method: "put",
      body: {
        context,
        carrier_configurations: values,
      },
      responseSchema: carrierConfigurationSchema,
    },
    meta,
  );
export const updateEmployerForm = (id: string, context: string, values: unknown, meta: RequestCallbacks = {}) => {
  const { address, mailAddress, sameAsMailingAddress, ...employer_form } = values;
  return request(
    {
      url: `/api/v2/employer-forms/${id}`,
      method: "put",
      body: {
        context,
        company_profile: {
          sameAsMailingAddress,
        },
        employer_form,
        address,
        mail_address: mailAddress,
      },
      responseSchema: employerFormSchema,
      responseKey: "employerForm",
    },
    meta,
  );
};
export const updateEmployeeForm = (id: string, context: string, values: unknown, meta: RequestCallbacks = {}) =>
  request(
    {
      url: `/api/v2/employee-forms/${id}`,
      method: "put",
      body: {
        context,
        employee_form: values,
      },
      responseSchema: employeeFormSchema,
      responseKey: "employeeForm",
    },
    meta,
  );
export const cancelCompanyPolicyForm = (context: string, values: unknown, meta: RequestCallbacks = {}) => {
  const companyIDToken = companyToken();
  const queryString = companyIDToken ? `?company_id_token=${companyIDToken}` : "";
  return request(
    {
      url: `/employer-dashboard/cancel-company-policy${queryString}`,
      method: "put",
      body: {
        cancel_company_policy_form: values,
        context,
      },
      responseSchema: companyPolicySchema,
      responseKey: "companyPolicy",
    },
    meta,
  );
};
export const terminateEmployee = (company_profile_id: string, id: string, context: string, values: unknown, meta: RequestCallbacks = {}) =>
  request(
    {
      url: `/api/v2/company_profiles/${company_profile_id}/terminate_employee/${id}`,
      method: "put",
      body: {
        context,
        employee_form: values,
      },
      responseSchema: employeeFormSchema,
      responseKey: "employeeForm",
    },
    meta,
  );
export const removeCoverage = (company_profile_id: string, id: string, values: unknown, meta: RequestCallbacks = {}) =>
  request(
    {
      url: `/api/v2/company_profiles/${company_profile_id}/remove_coverage/${id}`,
      method: "put",
      body: {
        employee_form: values,
      },
      responseSchema: employeePolicySchema,
      responseKey: "employeePolicy",
    },
    meta,
  );
export const getDependent = makeApiIdGetter("/api/v2/employee_dependents/:id", employeeDependentSchema);
export const updateDependent = (employee_form_id: string, id: string, values: unknown, meta: RequestCallbacks = {}) =>
  request(
    {
      url: `/api/v2/employee-forms/${employee_form_id}/employee-dependents/${id}/update-dependent`,
      method: "put",
      body: {
        employee_dependent: values,
      },
      responseSchema: employeeDependentSchema,
      responseKey: "employeeDependent",
    },
    meta,
  );
export const terminateEmployeeDependent = (employee_form_id: string, id: string, meta: RequestCallbacks = {}) =>
  request(
    {
      url: `/api/v2/employee-forms/${employee_form_id}/employee-dependents/${id}/delete-dependent`,
      method: "delete",
      responseSchema: employeeDependentSchema,
      responseKey: "employeeDependent",
    },
    meta,
  );
export const createClaim = (id: string, body: any, meta: RequestCallbacks = {}) =>
  request(
    {
      url: `/api/v2/employee-forms/${id}/claims`,
      method: "post",
      body,
      responseSchema: claimSchema,
      responseKey: "claim",
    },
    meta,
  );
export const getClaims = makeApiIdGetter(`/api/v2/employee-forms/:id/claims`, [claimSchema]);
export const getClaim = (employee_form_id: string, id: string, meta: RequestCallbacks = {}) =>
  request(
    {
      url: `/api/v2/employee-forms/${employee_form_id}/claims/${id}`,
      method: "get",
      responseSchema: claimSchema,
      responseKey: "claim",
    },
    meta,
  );
export const getCompanyPolicyPlans = makeApiIdGetter("/employer-dashboard/:id/plans", companyPolicySchema);
export const getEmployeeFormByFormId = makeApiIdGetter("/api/v2/employee-forms/:id", employeeFormSchema);
export const getInvoices = makeApiIdGetter("/api/v2/company-profiles/:id/invoices", [invoiceSchema]);
export const requestInvoice = (company_profile_id: string, body: any, meta: RequestCallbacks = {}) =>
  request(
    {
      url: `/api/v2/company-profiles/${company_profile_id}/request-invoices`,
      method: "post",
      body,
    },
    meta,
  );

export const getCarrierConfigByStateAndCarrier = (state: string, plan_brand_symbol: string, meta: RequestCallbacks = {}) =>
  request(
    {
      url: `/api/v2/carrier-configurations/find?state=${state}&plan_brand_symbol=${plan_brand_symbol}`,
      method: "get",
      responseSchema: carrierConfigurationSchema,
    },
    meta,
  );

export const upsertCarrierConfigurationRule = (
  context: string,
  values: {
    carrier_configuration_ids: Array<number | string>;
    type: string;
    flow: string;
    applies_to: string;
    data: any;
    notes?: string | null;
    source?: any | null;
    active?: boolean;
    apply_all_states?: boolean;
  },
  meta: RequestCallbacks = {},
) =>
  request(
    {
      url: `/api/v2/carrier-configuration-rules/upsert`,
      method: "post",
      body: {
        context,
        carrier_configuration_rule: values,
      },
      responseSchema: carrierConfigurationSchema,
    },
    meta,
  );

type CarrierConfigurationRuleParams = {
  type: string;
  flow: string;
  applies_to: string;
  data: any;
  notes?: string | null;
  source?: any | null;
  active?: boolean;
};

export const upsertCarrierConfigurationRules = (
  context: string,
  values: {
    carrier_configuration_ids: Array<number | string>;
    rules: CarrierConfigurationRuleParams[];
    apply_all_states?: boolean;
    deleted_rule_ids?: Array<number | string>;
  },
  meta: RequestCallbacks = {},
) =>
  request(
    {
      url: `/api/v2/carrier-configuration-rules/upsert`,
      method: "post",
      body: {
        context,
        carrier_configuration_rule: values,
      },
      responseSchema: carrierConfigurationSchema,
    },
    meta,
  );

export const queryCarrierConfigurationRule = (id: string | number, queries: [{ action: string; args: unknown[] }], meta: RequestCallbacks = {}) =>
  request(
    {
      url: `/api/v2/carrier-configuration-rules/${id}/query`,
      method: "post",
      body: { queries },
    },
    meta,
  );

export const deleteCarrierConfigurationRule = (id: string | number, meta: RequestCallbacks = {}) =>
  request(
    {
      url: `/api/v2/carrier-configuration-rules/${id}`,
      method: "delete",
      responseSchema: carrierConfigurationSchema,
    },
    meta,
  );
